/**
 * Tâches 1278 · 1279 (Eric 2026-06-29) — Injection sur TOUTE la bibliothèque.
 *
 * Un bouton et un décompte par sens :
 *   - Fireflies → fusion : ce que Fireflies a entendu et que Whisper a manqué ;
 *   - Whisper → fusion   : ce que Whisper a entendu et que la fusion a perdu.
 *
 * Rien n'est écrit avant l'aperçu « N vidéos · M passages » : on n'écrit pas
 * dans cinquante transcriptions sans savoir ce qu'on y met. Après coup, la
 * passe linguistique est proposée sur les SEULS passages ajoutés (1279) — le
 * texte importé arrive sans ponctuation soignée — avec le nombre d'appels IA
 * annoncé d'avance.
 *
 * Utilisé par le tableau de bord des écarts et par l'Explorateur de fichiers.
 */
import { useState } from "react";
import axios from "axios";
import { DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

import { API_BASE as API } from "../../lib/apiBase";
import { confirmDialog } from "../../lib/uiDialogs";
import { useLang } from "../../contexts/LanguageContext";

// Tâche 1327 (Eric 2026-06-30) — Les deux langues sur la même ligne : un
// sens d'injection ajouté plus tard ne peut pas partir avec un libellé
// traduit et une aide restée française.
const DIRECTIONS = [
  {
    key: "fireflies",
    label: ["Injecter Fireflies → fusion", "Inject Fireflies → merge"],
    help: [
      "Sur toutes les vidéos : le texte entendu par Fireflies là où Whisper a manqué le passage.",
      "On every video: the text Fireflies heard where Whisper missed the passage.",
    ],
  },
  {
    key: "whisper",
    label: ["Injecter Whisper → fusion", "Inject Whisper → merge"],
    help: [
      "Sur toutes les vidéos : le texte entendu par Whisper là où la fusion l'a perdu.",
      "On every video: the text Whisper heard where the merge lost it.",
    ],
  },
];

export const BulkGapInjectButtons = ({ onDone }) => {
  const { lang, tr } = useLang();
  const iLang = lang === "en" ? 1 : 0;
  const [previews, setPreviews] = useState({});
  const [busyDirection, setBusyDirection] = useState("");
  const [cleanup, setCleanup] = useState(null);

  const runLinguisticOnInjected = async (items) => {
    setCleanup({ done: 0, total: items.length });
    let corrections = 0;
    for (let i = 0; i < items.length; i += 1) {
      try {
        const { data: proposed } = await axios.post(
          `${API}/api/videos/${items[i].video_id}/propose-linguistic-corrections?scope=injected`,
          {}, { withCredentials: true, timeout: 180000 },
        );
        if ((proposed.corrections || []).length > 0) {
          await axios.post(
            `${API}/api/videos/${items[i].video_id}/apply-linguistic-corrections`,
            { corrections: proposed.corrections }, { withCredentials: true },
          );
          corrections += proposed.corrections.length;
        }
      } catch { /* une vidéo en échec n'arrête pas le lot */ }
      setCleanup({ done: i + 1, total: items.length });
    }
    setCleanup(null);
    toast.success(tr(
      `${corrections} correction(s) appliquée(s) sur les passages ajoutés`,
      `${corrections} correction(s) applied to the added passages`,
    ));
  };

  const runBulkInject = async (direction) => {
    setBusyDirection(direction);
    try {
      const { data: preview } = await axios.post(
        `${API}/api/videos/coverage-gaps/bulk-inject?direction=${direction}&dry_run=true`,
        {}, { withCredentials: true, timeout: 300000 },
      );
      setPreviews((p) => ({ ...p, [direction]: preview }));
      if (!preview.passages) {
        toast.info(tr(
          `Rien à injecter : aucun passage de ce type sur les ${preview.videos_scanned} vidéo(s) fusionnées.`,
          `Nothing to inject: no passage of this kind across the ${preview.videos_scanned} merged video(s).`,
        ));
        return;
      }
      const ok = await confirmDialog(
        tr(
          `${preview.videos_with_passages} vidéo(s) · ${preview.passages} passage(s) à injecter (${preview.label}). Chaque ajout reste retirable un par un. Injecter maintenant ?`,
          `${preview.videos_with_passages} video(s) · ${preview.passages} passage(s) to inject (${preview.label}). Every addition stays removable one by one. Inject now?`,
        ),
        { tone: "warning", confirmLabel: tr("Injecter", "Inject") },
      );
      if (!ok) return;
      const { data } = await axios.post(
        `${API}/api/videos/coverage-gaps/bulk-inject?direction=${direction}`,
        {}, { withCredentials: true, timeout: 600000 },
      );
      toast.success(tr(
        `${data.injected} passage(s) injecté(s) dans ${data.videos_with_passages} vidéo(s)`,
        `${data.injected} passage(s) injected into ${data.videos_with_passages} video(s)`,
      ));
      setPreviews((p) => ({ ...p, [direction]: { ...data, passages: 0 } }));
      if (data.injected > 0) {
        const clean = await confirmDialog(
          tr(
            `Nettoyer maintenant le texte ajouté (orthographe, accents, ponctuation) ? Le texte importé arrive sans ponctuation soignée. ${data.linguistic_calls_estimate} appel(s) IA facturé(s), un par vidéo, et uniquement sur les passages ajoutés.`,
            `Clean up the added text now (spelling, accents, punctuation)? Imported text arrives without careful punctuation. ${data.linguistic_calls_estimate} AI call(s) billed, one per video, and only on the added passages.`,
          ),
          { tone: "warning", confirmLabel: tr("Nettoyer", "Clean up") },
        );
        if (clean) await runLinguisticOnInjected(data.items || []);
      }
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Injection en lot impossible", "Bulk injection failed"));
    } finally {
      setBusyDirection("");
    }
  };

  return (
    <div data-testid="gaps-bulk-inject">
      <p className="text-[11px] text-zinc-500 mb-2">
        {tr(
          "Injection sur toutes les vidéos fusionnées de l'organisation. Un aperçu est affiché avant toute écriture ; chaque ajout reste retirable.",
          "Injection across every merged video of the organization. A preview is shown before anything is written; each addition stays removable.",
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {DIRECTIONS.map((d) => (
          <button
            key={d.key}
            onClick={() => runBulkInject(d.key)}
            disabled={!!busyDirection || !!cleanup}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-teal-300 bg-teal-50 text-teal-900 hover:bg-teal-100 disabled:opacity-50"
            data-testid={`gaps-bulk-inject-${d.key}`}
            title={d.help[iLang]}
          >
            <DownloadSimple size={14} />
            {busyDirection === d.key ? tr("Analyse en cours…", "Analyzing…") : d.label[iLang]}
            {previews[d.key] && (
              <span className="text-[10px] font-normal" data-testid={`gaps-bulk-count-${d.key}`}>
                · {previews[d.key].videos_with_passages} {tr("vidéo(s)", "video(s)")} · {previews[d.key].passages} {tr("passage(s)", "passage(s)")}
              </span>
            )}
          </button>
        ))}
        {cleanup && (
          <span className="text-[11px] text-blue-800" data-testid="gaps-bulk-cleanup-progress">
            {tr("Nettoyage linguistique : ", "Linguistic cleanup: ")}{cleanup.done}/{cleanup.total} {tr("vidéo(s)", "video(s)")}
          </span>
        )}
      </div>
    </div>
  );
};

export default BulkGapInjectButtons;
